import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form as FinalForm, Field } from 'react-final-form';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import * as validators from '../../util/validators';
import { getMediaUrl } from '../../util/uploadedMedia';
import {
  Modal,
  PrimaryButton,
  SecondaryButton,
  FieldTextInput,
  FieldSelect,
  LocationAutocompleteInputField,
  ImageFromFile,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  InlineTextButton,
  IconSpinner,
  AspectRatioWrapper,
  Icon,
} from '../../components';

import IconFacebook from './IconFacebook';
import IconInstagram from './IconInstagram';
import IconLinkedIn from './IconLinkedIn';
import css from './CreatorProfileWizardModal.module.css';

const ACCEPT_IMAGES = 'image/*';

// Helper to format location value for LocationAutocompleteInputField
const formatLocationValue = location => {
  if (!location) return '';
  if (typeof location === 'string') return location;
  if (location.search !== undefined) return location;
  if (location.address) {
    return {
      search: location.address,
      predictions: [],
      selectedPlace: location.selectedPlace || location,
    };
  }
  return '';
};

// Genre options from marketplace config
const GENRE_OPTIONS = [
  { key: 'circus', label: 'Circus' },
  { key: 'play', label: 'Play' },
  { key: 'dance', label: 'Dance' },
  { key: 'singing', label: 'Singing' },
  { key: 'magic', label: 'Magic' },
  { key: 'comedy', label: 'Comedy' },
  { key: 'dj', label: 'DJ' },
  { key: 'cabaret', label: 'Cabaret' },
  { key: 'opera', label: 'Opera' },
  { key: 'ballet', label: 'Ballet' },
  { key: 'cultural-heritage', label: 'Cultural heritage' },
  { key: 'puppetry', label: 'Puppetry' },
  { key: 'musical', label: 'Musical' },
  { key: 'variety', label: 'Variety' },
];

// Thumbnail Field for Step 2 - uses control menu like images panel
const ThumbnailField = props => {
  const { form, thumbnail, intl, isMenuOpen, onToggleMenu, onUpload, uploadInProgress } = props;

  // For newly selected files (not yet uploaded)
  const imageFromFile =
    thumbnail && thumbnail.lastModified ? (
      <ImageFromFile
        id={thumbnail.name}
        rootClassName={css.thumbnailImage}
        aspectWidth={4}
        aspectHeight={3}
        file={thumbnail}
      />
    ) : null;

  // For existing images (already uploaded)
  const existingImage =
    thumbnail && thumbnail?.id ? (
      <div className={css.thumbnailImage}>
        <AspectRatioWrapper width={4} height={3}>
          <img className={css.rootForImage} src={thumbnail.link} alt={thumbnail.name} />
        </AspectRatioWrapper>
      </div>
    ) : null;

  const hasImage = imageFromFile || existingImage;

  const handleRemoveThumbnail = () => {
    form.change('liveRoomThumbnail', null);
    form.change('liveRoomThumbnailImageId', null);
    onToggleMenu(false);
  };

  const controlMenu = hasImage ? (
    <Menu
      rootClassName={css.thumbnailControlMenu}
      isOpen={isMenuOpen}
      onToggleActive={onToggleMenu}
      useArrow={false}
      contentPosition="left"
      contentPlacementOffset={14}
    >
      <MenuLabel className={css.thumbnailMenuLabel}>
        <Icon name="more" />
      </MenuLabel>
      <MenuContent className={css.thumbnailMenuContent}>
        <MenuItem key="change" className={css.thumbnailMenuItem}>
          <InlineTextButton
            className={css.thumbnailMenuItemButton}
            type="button"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onToggleMenu(false);
              // Delay to let the menu close and DOM settle before triggering file input
              setTimeout(() => {
                const input = document.getElementById('liveRoomThumbnail');
                if (input) {
                  input.value = '';
                  input.click();
                }
              }, 100);
            }}
          >
            <FormattedMessage id="CreatorProfileWizardModal.changeThumbnail" />
          </InlineTextButton>
        </MenuItem>
        <MenuItem key="remove" className={css.thumbnailMenuItem}>
          <InlineTextButton
            className={css.thumbnailMenuItemButton}
            type="button"
            onClick={handleRemoveThumbnail}
          >
            <FormattedMessage id="CreatorProfileWizardModal.removeThumbnail" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  ) : null;

  const chooseThumbnailLabel = hasImage ? (
    <div className={css.thumbnailContainer}>
      {imageFromFile}
      {existingImage}
      {controlMenu}
    </div>
  ) : (
    <label className={css.thumbnailPlaceholder} htmlFor="liveRoomThumbnail">
      <AspectRatioWrapper width={4} height={3}>
        <span className={css.thumbnailPlaceholderContent}>
          <Icon name="image" size={48} className={css.thumbnailPlaceholderIcon} />
          <span className={css.thumbnailPlaceholderText}>
            <FormattedMessage id="CreatorProfileWizardModal.uploadThumbnail" />
          </span>
          <span className={css.thumbnailPlaceholderFormats}>
            <FormattedMessage id="CreatorProfileWizardModal.thumbnailFormats" />
          </span>
        </span>
      </AspectRatioWrapper>
    </label>
  );

  return (
    <div className={css.field}>
      <label className={css.fieldLabel}>
        <FormattedMessage id="CreatorProfileWizardModal.thumbnailLabel" />
      </label>
      <Field
        accept={ACCEPT_IMAGES}
        id="liveRoomThumbnail"
        name="liveRoomThumbnail"
        type="file"
        form={null}
      >
        {fieldProps => {
          const { accept, id, input } = fieldProps;
          const { name, type } = input;
          const onChange = async e => {
            const file = e.target.files[0];
            if (file) {
              // Set the file for preview immediately
              form.change('liveRoomThumbnail', file);

              // Upload the image to get the UUID
              if (onUpload) {
                try {
                  await onUpload({
                    id: `live-room-thumbnail-${Date.now()}`,
                    file: file,
                  });
                } catch (error) {
                  console.error('Error uploading thumbnail:', error);
                }
              }
            }
          };

          return (
            <div className={css.thumbnailWrapper}>
              {chooseThumbnailLabel}
              {uploadInProgress && (
                <div className={css.thumbnailUploadingOverlay}>
                  <IconSpinner className={css.thumbnailSpinner} />
                </div>
              )}
              <input
                accept={accept}
                id={id}
                name={name}
                className={css.thumbnailInput}
                onChange={onChange}
                type={type}
              />
            </div>
          );
        }}
      </Field>
    </div>
  );
};

const ProfileImageField = props => {
  const { form, profileImage, intl } = props;

  const imageFromFile =
    profileImage && profileImage.lastModified ? (
      <ImageFromFile
        id={profileImage.name}
        rootClassName={css.uploadedImage}
        aspectRatioClassName={css.squareAspectRatio}
        file={profileImage}
      />
    ) : null;

  const avatarComponent =
    profileImage && profileImage?.id ? (
      <div className={css.uploadedImage}>
        <div className={css.squareAspectRatio}>
          <img
            className={css.rootForImage}
            src={getMediaUrl(profileImage, 'card')}
            alt={profileImage.name}
          />
        </div>
      </div>
    ) : null;

  const hasImage = imageFromFile || avatarComponent;

  const chooseAvatarLabel = hasImage ? (
    <div className={css.avatarContainer}>
      {imageFromFile}
      {avatarComponent}
      <div className={css.changeAvatar}>
        <Icon name="edit" size={16} className={css.changeAvatarIcon} />
        <FormattedMessage id="ProfileImageForm.changeAvatar" />
      </div>
    </div>
  ) : (
    <div className={css.avatarPlaceholder}>
      <div className={css.avatarPlaceholderText}>
        <FormattedMessage id="ProfileImageForm.addYourProfilePicture" />
      </div>
      <div className={css.avatarPlaceholderTextMobile}>
        <FormattedMessage id="ProfileImageForm.addYourProfilePictureMobile" />
      </div>
    </div>
  );

  return (
    <div className={css.field}>
      <label className={css.fieldLabel}>
        <FormattedMessage id="CreatorProfileWizardModal.profilePhotoLabel" />
      </label>
      <Field accept={ACCEPT_IMAGES} id="profileImage" name="profileImage" type="file" form={null}>
        {fieldProps => {
          const { accept, id, input } = fieldProps;
          const { name, type } = input;
          const onChange = e => {
            const file = e.target.files[0];
            if (file) {
              form.change('profileImage', file);
            }
          };

          const triggerFileInput = () => {
            const input = document.getElementById(id);
            if (input) {
              input.value = '';
              input.click();
            }
          };

          return (
            <div className={css.profileImageWrapper}>
              <div
                className={css.profileImageLabel}
                onClick={triggerFileInput}
                role="button"
                tabIndex={0}
              >
                {chooseAvatarLabel}
              </div>
              <input
                accept={accept}
                id={id}
                name={name}
                className={css.profileImageInput}
                onChange={onChange}
                type={type}
              />
            </div>
          );
        }}
      </Field>
    </div>
  );
};

const SocialLinkField = props => {
  const { name, placeholder, icon, intl } = props;

  return (
    <div className={css.socialLinkField}>
      <FieldTextInput
        className={css.socialInput}
        type="text"
        id={name}
        name={name}
        placeholder={placeholder}
      />
      <span className={css.socialIcon}>{icon}</span>
    </div>
  );
};

// Step 2: Create Live Room
const Step2CreateLiveRoom = props => {
  const {
    intl,
    form,
    values,
    thumbnailMenuOpen,
    onToggleThumbnailMenu,
    onThumbnailUpload,
    thumbnailUploadInProgress,
  } = props;

  const liveRoomTitlePlaceholder = intl.formatMessage({
    id: 'CreatorProfileWizardModal.liveRoomTitlePlaceholder',
  });
  const liveRoomDescriptionPlaceholder = intl.formatMessage({
    id: 'CreatorProfileWizardModal.liveRoomDescriptionPlaceholder',
  });
  const genresPlaceholder = intl.formatMessage({
    id: 'CreatorProfileWizardModal.genresPlaceholder',
  });

  // Validation
  const liveRoomTitleRequiredMessage = intl.formatMessage({
    id: 'CreatorProfileWizardModal.liveRoomTitleRequired',
  });
  const liveRoomTitleRequired = validators.required(liveRoomTitleRequiredMessage);

  return (
    <div className={css.formFields}>
      <p className={css.stepDescription}>
        <FormattedMessage id="CreatorProfileWizardModal.createLiveRoomDescription" />
      </p>

      {/* Thumbnail */}
      <ThumbnailField
        form={form}
        thumbnail={values.liveRoomThumbnail}
        intl={intl}
        isMenuOpen={thumbnailMenuOpen}
        onToggleMenu={onToggleThumbnailMenu}
        onUpload={onThumbnailUpload}
        uploadInProgress={thumbnailUploadInProgress}
      />

      {/* Live Room Title */}
      <FieldTextInput
        className={css.field}
        type="text"
        id="liveRoomTitle"
        name="liveRoomTitle"
        label={intl.formatMessage({ id: 'CreatorProfileWizardModal.liveRoomTitleLabel' })}
        placeholder={liveRoomTitlePlaceholder}
        validate={liveRoomTitleRequired}
      />

      {/* Description */}
      <FieldTextInput
        className={css.field}
        type="textarea"
        id="liveRoomDescription"
        name="liveRoomDescription"
        label={intl.formatMessage({ id: 'CreatorProfileWizardModal.liveRoomDescriptionLabel' })}
        placeholder={liveRoomDescriptionPlaceholder}
      />

      {/* Genres (can inherit from Step 1 or override) */}
      <FieldSelect
        className={css.field}
        id="liveRoomGenres"
        name="liveRoomGenres"
        label={intl.formatMessage({ id: 'CreatorProfileWizardModal.genresLabel' })}
        options={GENRE_OPTIONS}
        placeholder={genresPlaceholder}
      />
    </div>
  );
};

const CreatorProfileWizardModalComponent = props => {
  const {
    id,
    intl,
    isOpen,
    onClose,
    onSubmit,
    onBack,
    onSkip,
    onManageDisableScrolling,
    submitInProgress,
    submitError,
    initialValues,
    currentStep,
    totalSteps,
    onThumbnailUpload,
    thumbnailUploadInProgress,
  } = props;

  const [thumbnailMenuOpen, setThumbnailMenuOpen] = useState(false);

  const step = currentStep || 1;
  const total = totalSteps || 2;
  const progressPercentage = (step / total) * 100;

  // Validation messages
  const artistAliasRequiredMessage = intl.formatMessage({
    id: 'CreatorProfileWizardModal.artistAliasRequired',
  });
  const artistAliasRequired = validators.required(artistAliasRequiredMessage);

  // Placeholders
  const artistAliasPlaceholder = intl.formatMessage({
    id: 'CreatorProfileWizardModal.artistAliasPlaceholder',
  });
  const genresPlaceholder = intl.formatMessage({
    id: 'CreatorProfileWizardModal.genresPlaceholder',
  });
  const locationPlaceholder = intl.formatMessage({
    id: 'CreatorProfileWizardModal.locationPlaceholder',
  });
  const bioPlaceholder = intl.formatMessage({
    id: 'CreatorProfileWizardModal.bioPlaceholder',
  });
  const facebookPlaceholder = intl.formatMessage({
    id: 'CreatorProfileWizardModal.facebookPlaceholder',
  });
  const instagramPlaceholder = intl.formatMessage({
    id: 'CreatorProfileWizardModal.instagramPlaceholder',
  });
  const linkedinPlaceholder = intl.formatMessage({
    id: 'CreatorProfileWizardModal.linkedinPlaceholder',
  });

  return (
    <Modal
      id={id}
      className={css.modalWrapper}
      scrollLayerClassName={css.scrollLayer}
      containerClassName={css.modalContainer}
      contentClassName={css.modalContent}
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
    >
      <FinalForm
        onSubmit={onSubmit}
        initialValues={initialValues}
        keepDirtyOnReinitialize={true}
        render={formRenderProps => {
          const { handleSubmit, form, values, submitting, pristine, errors } = formRenderProps;
          const profileImage = values.profileImage;

          // Step-specific validation
          const isStep1Invalid =
            step === 1 && (!values.artistAlias || values.artistAlias.trim() === '');
          const isStep2Invalid =
            step === 2 && (!values.liveRoomTitle || values.liveRoomTitle.trim() === '');
          const hasStepValidationError = isStep1Invalid || isStep2Invalid;

          const submitDisabled = submitting || submitInProgress || hasStepValidationError;

          // Skip allowed on every step — opting out at any point
          // dismisses the wizard permanently (creator-side requirement).
          const showSkipButton = !!onSkip;

          // Get step-specific title
          const getStepTitle = () => {
            switch (step) {
              case 1:
                return intl.formatMessage({ id: 'CreatorProfileWizardModal.step1Title' });
              case 2:
                return intl.formatMessage({ id: 'CreatorProfileWizardModal.step2Title' });
              default:
                return intl.formatMessage({ id: 'CreatorProfileWizardModal.step1Title' });
            }
          };

          // Render Step 1 content - Creator Profile
          const renderStep1Content = () => (
            <div className={css.formFields}>
              {/* Profile Photo */}
              <ProfileImageField form={form} profileImage={profileImage} intl={intl} />

              {/* Name / Artist Alias */}
              <FieldTextInput
                className={css.field}
                type="text"
                id="artistAlias"
                name="artistAlias"
                label={intl.formatMessage({ id: 'CreatorProfileWizardModal.artistAliasLabel' })}
                placeholder={artistAliasPlaceholder}
                validate={artistAliasRequired}
              />

              {/* Bio */}
              <FieldTextInput
                className={css.field}
                type="textarea"
                id="bio"
                name="bio"
                label={intl.formatMessage({ id: 'CreatorProfileWizardModal.bioLabel' })}
                placeholder={bioPlaceholder}
              />

              {/* Genres */}
              <FieldSelect
                className={css.field}
                id="genres"
                name="genres"
                label={intl.formatMessage({ id: 'CreatorProfileWizardModal.genresLabel' })}
                options={GENRE_OPTIONS}
                placeholder={genresPlaceholder}
              />

              {/* Location */}
              <div className={css.field}>
                <label className={css.fieldLabel}>
                  <FormattedMessage id="CreatorProfileWizardModal.locationLabel" />
                </label>
                <LocationAutocompleteInputField
                  className={css.locationField}
                  inputClassName={css.locationInput}
                  iconClassName={css.locationIcon}
                  predictionsClassName={css.locationPredictions}
                  name="location"
                  placeholder={locationPlaceholder}
                  useDefaultPredictions={false}
                  format={v => v}
                  valueFromForm={formatLocationValue(values.location)}
                />
              </div>

              {/* Social Links */}
              <div className={css.field}>
                <label className={css.fieldLabel}>
                  <FormattedMessage id="CreatorProfileWizardModal.socialLinksLabel" />
                </label>
                <div className={css.socialLinks}>
                  <SocialLinkField
                    name="facebookLink"
                    placeholder={facebookPlaceholder}
                    icon={<IconFacebook />}
                    intl={intl}
                  />
                  <SocialLinkField
                    name="instagramLink"
                    placeholder={instagramPlaceholder}
                    icon={<IconInstagram />}
                    intl={intl}
                  />
                  <SocialLinkField
                    name="linkedinLink"
                    placeholder={linkedinPlaceholder}
                    icon={<IconLinkedIn />}
                    intl={intl}
                  />
                </div>
              </div>
            </div>
          );

          // Render step content based on current step
          const renderStepContent = () => {
            switch (step) {
              case 1:
                return renderStep1Content();
              case 2:
                return (
                  <Step2CreateLiveRoom
                    intl={intl}
                    form={form}
                    values={values}
                    thumbnailMenuOpen={thumbnailMenuOpen}
                    onToggleThumbnailMenu={setThumbnailMenuOpen}
                    onThumbnailUpload={onThumbnailUpload}
                    thumbnailUploadInProgress={thumbnailUploadInProgress}
                  />
                );
              default:
                return renderStep1Content();
            }
          };

          // Get button text based on step
          const getSubmitButtonText = () => {
            if (step === total) {
              return intl.formatMessage({ id: 'CreatorProfileWizardModal.finishButton' });
            }
            return intl.formatMessage({ id: 'CreatorProfileWizardModal.nextButton' });
          };

          return (
            <form onSubmit={handleSubmit} className={css.form}>
              <div className={css.content}>
                {/* Progress bar */}
                <div className={css.stepHeader}>
                  <div className={css.progressBar}>
                    <div className={css.progressLine} style={{ width: `${progressPercentage}%` }} />
                  </div>
                  <span className={css.stepText}>
                    <FormattedMessage
                      id="CreatorProfileWizardModal.stepOf"
                      values={{ step, total }}
                    />
                  </span>
                </div>

                {/* Title */}
                <h2 className={css.title}>{getStepTitle()}</h2>

                {/* Step content */}
                {renderStepContent()}
              </div>

              {/* Footer */}
              <div className={css.footer}>
                {showSkipButton && (
                  <InlineTextButton className={css.skipButton} type="button" onClick={onSkip}>
                    <FormattedMessage id="CreatorProfileWizardModal.skipButton" />
                  </InlineTextButton>
                )}
                <div className={css.footerButtons}>
                  {step > 1 && onBack && (
                    <SecondaryButton className={css.backButton} type="button" onClick={onBack}>
                      <FormattedMessage id="CreatorProfileWizardModal.backButton" />
                    </SecondaryButton>
                  )}
                  <PrimaryButton
                    className={css.submitButton}
                    inProgressClassName={css.submitButtonInProgress}
                    type="submit"
                    inProgress={submitting || submitInProgress}
                    disabled={submitDisabled}
                  >
                    {getSubmitButtonText()}
                  </PrimaryButton>
                </div>
              </div>
            </form>
          );
        }}
      />
    </Modal>
  );
};

CreatorProfileWizardModalComponent.defaultProps = {
  isOpen: false,
  submitInProgress: false,
  submitError: null,
  initialValues: {},
  currentStep: 1,
  totalSteps: 2,
  onBack: null,
  onSkip: null,
};

CreatorProfileWizardModalComponent.propTypes = {
  id: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  onSkip: PropTypes.func,
  onManageDisableScrolling: PropTypes.func.isRequired,
  submitInProgress: PropTypes.bool,
  submitError: PropTypes.object,
  initialValues: PropTypes.object,
  currentStep: PropTypes.number,
  totalSteps: PropTypes.number,
};

const CreatorProfileWizardModal = injectIntl(CreatorProfileWizardModalComponent);

export default CreatorProfileWizardModal;
